import { getMipInvestorBankAccounts } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccounts.api.ts';
import { useMantineTableDataOperationsFromUrlParamState } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import {
	BankAccountValidationOutcomeFilter,
	BankAccountVerificationBucketFilter,
	InvestorBankAccountFilterQueryDto,
	InvestorBankAccountSortOrder,
	InvestorBankAccountSortingQueryDto,
	ListInvestorBankAccountsDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	getEnumArrayFilter,
	getTextFilter,
	SORT_FIELD_BY_COLUMN_ID,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/useBankAccountsController.utils.ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export const useBankAccountsController = () => {
	const dataOperations = useMantineTableDataOperationsFromUrlParamState({});
	const { columnFilters, globalSearchString, sorting, pagination } = dataOperations;

	const filterQuery = useMemo<InvestorBankAccountFilterQueryDto>(
		() => ({
			accountHolder: getTextFilter(columnFilters, 'accountHolder'),
			ownerName: getTextFilter(columnFilters, 'ownerName'),
			verificationStatuses: getEnumArrayFilter(
				columnFilters,
				'verificationStatus',
				Object.values(BankAccountVerificationBucketFilter)
			),
			validationOutcomes: getEnumArrayFilter(
				columnFilters,
				'validation',
				Object.values(BankAccountValidationOutcomeFilter)
			),
		}),
		[columnFilters]
	);

	const sortingQuery = useMemo<InvestorBankAccountSortingQueryDto | undefined>(() => {
		const [first] = sorting;
		const field = first && SORT_FIELD_BY_COLUMN_ID[first.id];

		if (!field) {
			return undefined;
		}

		return {
			field,
			order: first.desc
				? InvestorBankAccountSortOrder.DESC
				: InvestorBankAccountSortOrder.ASC,
		};
	}, [sorting]);

	const request = useMemo<ListInvestorBankAccountsDto>(
		() => ({
			offset: pagination.pageIndex * pagination.pageSize,
			limit: pagination.pageSize,
			searchQuery: globalSearchString || undefined,
			filterQuery,
			sortingQuery,
		}),
		[pagination.pageIndex, pagination.pageSize, globalSearchString, filterQuery, sortingQuery]
	);

	const bankAccountsQuery = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_BANK_ACCOUNT_LIST_POST, request],
		queryFn: ({ signal }) => getMipInvestorBankAccounts(request, { abortSignal: signal }),
		placeholderData: keepPreviousData,
	});

	return {
		dataOperations,
		request,
		bankAccounts: bankAccountsQuery.data?.payload?.bankAccounts ?? [],
		stats: bankAccountsQuery.data?.payload?.stats,
		isLoading: bankAccountsQuery.isLoading,
		isFetching: bankAccountsQuery.isFetching,
	};
};
